// Service-worker registration for the Lithos Field PWA.
//
// vite.config.ts runs vite-plugin-pwa in `injectManifest` mode with
// `injectRegister: false`, so the plugin does NOT auto-inject registration —
// we own it here. This module uses the plugin's `virtual:pwa-register` helper,
// which knows the built SW filename (`/sw.js`) and scope, and wires up
// `registerType: 'prompt'` so a new SW WAITS instead of auto-activating. The
// returned `updateSW` function (which posts SKIP_WAITING to the waiting SW and
// reloads on controllerchange) is stashed via setUpdateSW so the field shell's
// update banner can trigger it when the user taps "Update".
//
// Office pages (anything outside /field) never reload on their own: when a new
// SW takes over (sw.ts skips waiting if no field window is open) they flag the
// update and the office banner (OfficeUpdateBanner) offers "Reload" — card
// 5gncDWpbA9KV, so a background deploy can't wipe a half-filled PO wizard.
//
// Called once from entry-client.tsx at boot. Safe to call in any environment:
//   - Guards on `'serviceWorker' in navigator`.
//   - In dev (`devOptions.enabled: false`) the virtual module is a no-op stub,
//     so nothing is registered and there is no 404.

import { isOfficeReloadRequested, setUpdateSW, setUpdateState } from './pwaUpdate.js';

// Long-open tabs only notice a deploy on navigation; check hourly as well.
const UPDATE_CHECK_INTERVAL_MS = 60 * 60 * 1000;

let started = false;

export function registerFieldServiceWorker(): void {
  if (started) return;
  started = true;

  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) {
    return;
  }

  // When a newly-deployed SW takes control, a FIELD page reloads once onto the
  // latest bundle (it only gets here after the user tapped Update — sw.ts
  // waits while a field window is open). An OFFICE page reloads only if the
  // user asked from the office banner; otherwise it flags the update so the
  // banner shows "Reload". Guarded two ways: `reloading` prevents a reload
  // loop, and nothing happens unless the page was ALREADY controlled by a SW
  // (the very first install has no old code to replace).
  const hadController = Boolean(navigator.serviceWorker.controller);
  let reloading = false;
  navigator.serviceWorker.addEventListener('controllerchange', (event) => {
    if (!hadController || reloading) return;
    const isField = window.location.pathname.startsWith('/field');
    if (!isField && !isOfficeReloadRequested()) {
      setUpdateState('active');
      // Once this page has seen a waiting SW, the plugin's workbox-window
      // instance (registered below, so its listener runs after this one)
      // reloads on controllerchange — e.g. when the user taps Update in a
      // /field tab of the same browser. Stop it reaching that listener.
      event.stopImmediatePropagation();
      return;
    }
    reloading = true;
    window.location.reload();
  });

  // Dynamic import so SSR / any non-browser bootstrap never evaluates the
  // virtual module, and so a missing SW build can't break the entry bundle.
  import('virtual:pwa-register')
    .then(({ registerSW }) => {
      const updateSW = registerSW({
        immediate: true,
        onRegisteredSW(swUrl, registration) {
          console.log('[PWA] Service worker registered:', swUrl);
          if (registration) {
            setInterval(() => void registration.update().catch(() => {}), UPDATE_CHECK_INTERVAL_MS);
          }
        },
        onNeedRefresh() {
          // New content is available; surface it via the same custom event the
          // usePWA hook already listens for, and to the office banner.
          setUpdateState('waiting');
          window.dispatchEvent(new CustomEvent('pwa-update-available'));
        },
        onOfflineReady() {
          console.log('[PWA] App ready to work offline');
        },
        onRegisterError(error) {
          console.error('[PWA] Service worker registration failed:', error);
        },
      });
      // Stash the update trigger so the field shell's banner can apply the
      // waiting update on user tap (prompt mode — see client/field/pwaUpdate.ts).
      setUpdateSW(updateSW);
    })
    .catch((error) => {
      console.error('[PWA] Failed to load SW registration module:', error);
    });
}
